import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { useFloating } from '@floating-ui/react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Portal } from '@trinity/chat-components/components/Portal';
import {
  DropdownMenu,
  DropdownMenuAnchor,
} from '@trinity/chat-components/components/presentation/DropdownMenu';
import {
  useZIndexMiddleware,
  useSameWidthMiddleware,
} from '@trinity/chat-components/components/presentation/popper-modifiers';
import { Constants } from '@trinity/chat-components/components/Theme';
import { useChatAgentMenuItems } from './hooks/useChatAgentMenuItems';
import { ContextSelectorStyles } from './styles';
import { useContextSelectoryDisplayValue } from './hooks/useContextSelectoryDisplayValue';

export const SimpleContextSelector = () => {
  const [open, setOpen] = useState<boolean>(false);

  const zIndexMiddleware = useZIndexMiddleware();
  const sameWidthMiddleware = useSameWidthMiddleware();
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [zIndexMiddleware, sameWidthMiddleware],
  });

  const handleBlur = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleItemClick = useCallback(() => {
    handleBlur();
  }, [handleBlur]);

  const menuItems = useChatAgentMenuItems({
    onClick: handleItemClick,
  });

  const handleClick = useCallback(
    (_: React.MouseEvent) => {
      if (!menuItems) return;

      setOpen((prev) => !prev);
      // e.stopPropagation();
    },
    [menuItems]
  );

  const label = useContextSelectoryDisplayValue();

  return (
    <Root>
      <Wrap>
        <StyledDropdownMenuAnchor ref={refs.setReference} onClick={handleClick} open={open}>
          {label}
        </StyledDropdownMenuAnchor>
      </Wrap>
      {open && (
        <Portal>
          <ClickAwayListener onClickAway={handleBlur}>
            <StyledDropdownMenu ref={refs.setFloating} style={floatingStyles}>
              {menuItems}
            </StyledDropdownMenu>
          </ClickAwayListener>
        </Portal>
      )}
    </Root>
  );
};

const Root = styled.div({});

const Wrap = styled.div({
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
});

const StyledDropdownMenu = styled(DropdownMenu)({
  maxWidth: 400,
});

const StyledDropdownMenuAnchor = styled(DropdownMenuAnchor)(({ theme }) => ({
  height: Constants.Header.ItemHeight,
  color: theme.palette.black.main,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  ...ContextSelectorStyles,
}));
